import * as React from "react"
import { router } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { Pencil, Trash2 } from "lucide-react"
import { ConfirmDialog } from "@/components/flashcards/ConfirmDialog"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { QuestionForm } from "./QuestionForm"
import { QuestionList } from "./QuestionList"
import type { ContentAreaGroup } from "@/types/flashcards"
import type { Question, QuizDetail } from "@/types/quizzes"

/**
 * Question list with add / edit / delete for a quiz the viewer can manage.
 * `basePath` is the quiz's URL ("/quizzes/1" or "/admin/quizzes/1").
 */
export function QuizEditor({
  quiz,
  basePath,
  contentAreaGroups,
}: {
  quiz: QuizDetail
  basePath: string
  contentAreaGroups: ContentAreaGroup[]
}) {
  const intl = useIntl()
  const [editing, setEditing] = React.useState<Question | null>(null)
  const [deleting, setDeleting] = React.useState<Question | null>(null)
  const [processing, setProcessing] = React.useState(false)
  const requireExplanation = quiz.official

  const confirmDelete = () => {
    if (!deleting) return
    router.delete(`${basePath}/questions/${deleting.id}`, {
      preserveScroll: true,
      onStart: () => setProcessing(true),
      onFinish: () => {
        setProcessing(false)
        setDeleting(null)
      },
    })
  }

  return (
    <>
      <section className="mt-8">
        <h2>{intl.formatMessage({ id: "quizzes.add_title" })}</h2>
        <div className="callout mt-4">
          <QuestionForm
            idPrefix="new-question"
            action={`${basePath}/questions`}
            method="post"
            contentAreaGroups={contentAreaGroups}
            requireExplanation={requireExplanation}
            submitLabel={intl.formatMessage({ id: "quizzes.add_question" })}
          />
        </div>
      </section>

      <section className="mt-10">
        <h2>{intl.formatMessage({ id: "quizzes.questions" })}</h2>
        {quiz.questions.length === 0 ? (
          <p className="mt-2">{intl.formatMessage({ id: "quizzes.no_questions" })}</p>
        ) : (
          <QuestionList
            questions={quiz.questions}
            actions={(question) => (
              <div className="flex shrink-0 items-center gap-1">
                <Button type="button" variant="ghost" size="icon" aria-label={intl.formatMessage({ id: "quizzes.edit_question" })} onClick={() => setEditing(question)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" aria-label={intl.formatMessage({ id: "quizzes.delete_question" })} onClick={() => setDeleting(question)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          />
        )}
      </section>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>{intl.formatMessage({ id: "quizzes.edit_question" })}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="mt-4">
              <QuestionForm
                key={editing.id}
                idPrefix="edit-question"
                action={`${basePath}/questions/${editing.id}`}
                method="patch"
                question={editing}
                contentAreaGroups={contentAreaGroups}
                requireExplanation={requireExplanation}
                submitLabel={intl.formatMessage({ id: "quizzes.save_question" })}
                onSuccess={() => setEditing(null)}
                onCancel={() => setEditing(null)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={intl.formatMessage({ id: "quizzes.delete_question_title" })}
        description={intl.formatMessage({ id: "quizzes.delete_question_body" })}
        confirmLabel={intl.formatMessage({ id: "quizzes.delete_question" })}
        onConfirm={confirmDelete}
        processing={processing}
      />
    </>
  )
}
